import { useEffect, useMemo, useState } from 'react';
import { TimeGridQuery } from '../../../types/planung-graphql-client-defs';
import {
  buildDefaultMapping,
  countCardsPerEntry,
  EntryMapping,
  MigrationCard,
  previewCardMigration,
} from '../../../utils/timeGridMigration';

export type MigrationTimeGrid = TimeGridQuery['timeGrids'][number];

export const NO_TARGET = '';

/**
 * Zustand der Stunden-Zuordnung zwischen zwei Zeitrastern samt Vorschau der Kartenmigration.
 * Wechselt eines der Raster, startet die Zuordnung wieder mit dem Positions-Vorschlag.
 */
export const useTimeGridMigration = ({
  cards,
  newTimeGrid,
  oldTimeGrid,
}: {
  cards: MigrationCard[];
  newTimeGrid: MigrationTimeGrid | undefined;
  oldTimeGrid: MigrationTimeGrid | undefined;
}) => {
  const [mapping, setMapping] = useState<EntryMapping>({});
  const [resetAvailabilities, setResetAvailabilities] = useState(false);

  const oldEntries = useMemo(() => oldTimeGrid?.entries ?? [], [oldTimeGrid]);
  const newEntries = useMemo(() => newTimeGrid?.entries ?? [], [newTimeGrid]);

  useEffect(() => {
    setMapping(buildDefaultMapping(oldEntries, newEntries));
    setResetAvailabilities(false);
  }, [oldEntries, newEntries]);

  const cardsPerEntry = useMemo(() => countCardsPerEntry(cards, oldEntries), [cards, oldEntries]);
  const preview = useMemo(
    () => previewCardMigration({ cards, mapping, newEntries, oldEntries }),
    [cards, mapping, newEntries, oldEntries],
  );

  const setTarget = (oldEntryUuid: string, targetUuid: string) => {
    setMapping((prev) => {
      // Eine neue Stunde kann nur Ziel EINER alten Stunde sein – ein schon belegtes Ziel wird nicht umgehängt.
      const isTakenByOther = Object.entries(prev).some(([from, to]) => from !== oldEntryUuid && to === targetUuid);
      if (isTakenByOther) return prev;
      const { [oldEntryUuid]: _previous, ...next } = prev;
      if (targetUuid !== NO_TARGET) next[oldEntryUuid] = targetUuid;
      return next;
    });
  };

  const mappingInput = Object.entries(mapping).map(([fromTimeGridEntryUuid, toTimeGridEntryUuid]) => ({
    fromTimeGridEntryUuid,
    toTimeGridEntryUuid,
  }));

  return {
    cardsPerEntry,
    mapping,
    mappingInput,
    newEntries,
    oldEntries,
    preview,
    resetAvailabilities,
    setResetAvailabilities,
    setTarget,
  };
};

export type TimeGridMigrationState = ReturnType<typeof useTimeGridMigration>;
